import { useEffect } from 'react';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';

type BrowserConfig = {
  licenseKey: string;
  applicationID: string;
  accountID: string;
  agentID: string;
  trustKey: string;
};

/**
 * Initializes the New Relic Browser agent once its config is available.
 * Renders nothing.
 *
 * The config is fetched from the system-overview backend at request time
 * rather than read via `configApiRef` — a `@visibility frontend` app-config
 * value gets baked into the static frontend bundle at Docker build time,
 * before the deploy-time env vars (injected by Helm into the running pod)
 * exist, so it would always resolve empty on the one image built and shared
 * across dev/uat/prod.
 */
export const NewRelicBrowserAgent = () => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const baseUrl = await discoveryApi.getBaseUrl('system-overview');
      const res = await fetchApi.fetch(`${baseUrl}/newrelic-browser-config`);
      if (!res.ok || cancelled) return;

      const { licenseKey, applicationID, accountID, agentID, trustKey } =
        (await res.json()) as BrowserConfig;

      const { BrowserAgent } = await import(
        '@newrelic/browser-agent/loaders/browser-agent'
      );
      if (cancelled) return;

      // eslint-disable-next-line no-new -- BrowserAgent's constructor starts monitoring as a side effect
      new BrowserAgent({ // NOSONAR: typescript:S1848 — same intentional side-effecting construction
        info: {
          // This account's New Relic platform is provisioned in the EU region
          // (same as the newrelic.ts proxy's NEW_RELIC_BASE_URL) — the
          // library defaults to the US collector, which 403s a EU-only
          // license key.
          beacon: 'bam.eu01.nr-data.net',
          errorBeacon: 'bam.eu01.nr-data.net',
          licenseKey,
          applicationID,
          sa: 1,
        },
        loader_config: {
          accountID,
          trustKey,
          agentID,
          licenseKey,
          applicationID,
        },
        init: {
          distributed_tracing: { enabled: true },
          privacy: { cookies_enabled: true },
          ajax: { deny_list: [] },
        },
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [discoveryApi, fetchApi]);

  return null;
};
